import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Modal,
  Fade,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LogoPrefeitura from "../../assets/images/logo-prefeitura.png";
import { loginCandidato } from "../../services/candidato";
import { formatarCpf, validarCpf } from "../../utils/normalization";
import type { LoginRequest } from "../../types";
import { useAuth } from "../../contexts/AuthContext";

const LoginCandidato: React.FC = () => {
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();

  const [openModal, setOpenModal] = useState(false);
  const [candidatoId, setCandidatoId] = useState<number | null>(null);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleCloseModal = () => {
    setOpenModal(false);
    setTimeout(() => {
      if (candidatoId !== null) {
        navigate("/painel-vagas", {
          state: { candidatoId: candidatoId },
        });
      } else {
        navigate("/painel-vagas");
      }
    }, 300);
  };

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar CPF
    const cpfNumerico = cpf.replace(/\D/g, "");
    if (!validarCpf(cpfNumerico)) {
      setErrorMessage("CPF inválido! Por favor, insira um CPF válido.");
      setOpenErrorModal(true);
      return;
    }

    const loginData: LoginRequest = {
      cpf: cpfNumerico,
      senha: senha,
    };

    try {
      const response = await loginCandidato(loginData);

      // O backend retorna ApiResponse<LoginResponse>
      if (response.data?.success && response.data?.data) {
        const loginInfo = response.data.data;

        // Usar o AuthContext para fazer login
        const userData = {
          id: loginInfo.id,
          nome: loginInfo.nome,
          email: loginInfo.email,
          telefone: loginInfo.telefone,
          tipo: 'candidato' as const,
          cpf: cpfNumerico
        };

        // Salvar no AuthContext
        login(loginInfo.token, userData);

        setCandidatoId(loginInfo.id);
        setOpenModal(true);
      } else {
        // Se não teve sucesso, usar a mensagem de erro do backend
        const errorMessage = response.data?.message || "Erro ao fazer login";
        setErrorMessage(errorMessage);
        setOpenErrorModal(true);
      }
    } catch (error: any) {
      console.error("Erro ao fazer login:", error);

      // Verificar se há resposta do backend
      if (error?.response?.data) {
        const apiResponse = error.response.data;

        // Se a resposta tem o formato ApiResponse
        if (apiResponse.message) {
          setErrorMessage(apiResponse.message);
        } else {
          setErrorMessage("Erro ao fazer login. Verifique seus dados.");
        }
      } else if (error?.response?.status === 401) {
        setErrorMessage("CPF ou senha incorretos.");
      } else if (error?.response?.status === 403) {
        setErrorMessage(
          "❌ USUÁRIO INATIVO\n\nSua conta foi inativada. Para reativar seu acesso, procure a Prefeitura Local."
        );
      } else {
        setErrorMessage("Erro de conexão com o servidor.");
      }
      setOpenErrorModal(true);
    }
  };

  const isFormValid = () => {
    const cpfNumbers = cpf.replace(/\D/g, "");
    return cpfNumbers.length === 11 && senha.trim().length >= 6;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 5,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 2,
            backgroundColor: "#ffffff",
            maxWidth: 450,
            margin: "0 auto",
            minHeight: 600,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={LogoPrefeitura}
            alt="Logo Prefeitura"
            sx={{
              width: 240,
              height: "auto",
              mb: 3,
              objectFit: "contain",
            }}
          />

          {/* Título */}
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, mb: 0.5, color: "#333" }}
          >
            Portal de Vagas
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: "#666" }}>
            Prefeitura de Bom Retiro do Sul
          </Typography>

          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 4, color: "#333" }}
          >
            Login do Candidato
          </Typography>

          {/* Formulário */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="CPF"
                value={cpf}
                onChange={(e) => {
                  const formatted = formatarCpf(e.target.value);
                  setCpf(formatted);
                }}
                margin="normal"
                required
                placeholder="000.000.000-00"
                variant="outlined"
                autoFocus
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                margin="normal"
                required
                placeholder="Digite sua senha"
                variant="outlined"
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderWidth: 1,
                    },
                    "&:hover fieldset": {
                      borderColor: "#bdbdbd",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666",
                    fontSize: "0.875rem",
                    "&.Mui-focused": {
                      color: "#1976d2",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px 14px",
                    color: "#333",
                    "&::placeholder": {
                      color: "#999",
                      opacity: 1,
                    },
                  },
                }}
              />

              <Box sx={{ textAlign: "right", mt: 0.5, mb: 3 }}>
                <Button
                  variant="text"
                  onClick={() => navigate("/recuperar-senha-candidato")}
                  sx={{
                    textTransform: "none",
                    color: "#1976d2",
                    fontSize: "0.875rem",
                    fontWeight: 400,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Esqueceu sua senha?
                </Button>
              </Box>
            </Box>

            <Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!isFormValid()}
                sx={{
                  mt: 1,
                  mb: 2,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  backgroundColor: "#2196f3",
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#999",
                  },
                }}
              >
                ENTRAR
              </Button>

              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                  Não possui cadastro?{" "}
                  <Button
                    variant="text"
                    onClick={() => navigate("/vagas")}
                    sx={{
                      textTransform: "none",
                      color: "#1976d2",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Cadastrar
                  </Button>
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Sou empresa?{" "}
                  <Button
                    variant="text"
                    onClick={() => navigate("/login-empresa")}
                    sx={{
                      textTransform: "none",
                      color: "#1976d2",
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Login Empresa
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropProps={{
          sx: {
            backdropFilter: "blur(5px)",
            bgcolor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 60, color: "success.main", mb: 2 }}
            />
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              fontWeight="bold"
            >
              Login Realizado!
            </Typography>
            <Typography id="transition-modal-description" sx={{ mt: 2 }}>
              Redirecionando para o painel de vagas...
            </Typography>
            <Button
              variant="contained"
              onClick={handleCloseModal}
              sx={{ mt: 3 }}
            >
              OK
            </Button>
          </Box>
        </Fade>
      </Modal>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openErrorModal}
        onClose={handleCloseErrorModal}
        closeAfterTransition
        BackdropProps={{
          sx: {
            backdropFilter: "blur(5px)",
            bgcolor: "rgba(0, 0, 0, 0.5)",
          },
        }}
      >
        <Fade in={openErrorModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              border: "2px solid #000",
              boxShadow: 24,
              p: 4,
              textAlign: "center",
              borderRadius: 2,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 60, color: "error.main", mb: 2 }}
            />
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              fontWeight="bold"
            >
              Erro no Login
            </Typography>
            <Typography id="transition-modal-description" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={handleCloseErrorModal}
              sx={{ mt: 3 }}
            >
              Fechar
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default LoginCandidato;